import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';
import { requireOrgId, stripImmutable } from '../lib/orgScope';

export interface SubTask {
  id: string;
  text: string;
  completed: boolean;
}

export interface Comment {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  tags: string[];
  status: string;
  priority?: 'lowest' | 'low' | 'medium' | 'high' | 'highest';
  dueDate?: string;
  hasAttachment?: boolean;
  hasChecklist?: boolean;
  image?: string;
  url?: string[];
  drive?: string[];
  figma?: string[];
  jira?: string[];
  docs?: string[];
  notes?: string;
  subtasks?: SubTask[];
  createdAt?: string;
  reminder?: string;
  comments?: Comment[];
  project?: string;
  assignee?: string;
  startDate?: string;
  sprint?: string;
  storyPoints?: string;
  reporter?: string;
}

export interface Column {
  id: string;
  title: string;
  color: string;
  tasks: Task[];
}

export interface SavedTaskBoard {
  id: string;
  user_id: string;
  org_id: string;
  name: string;
  columns: Column[];
  project_id?: string | null;
  created_at: string;
  updated_at: string;
}

// ── In-memory caches (kept so other imports keep working) ──────────────────
export let _boardsCache: SavedTaskBoard[] | null = null;
let _boardsCacheExpiry = 0;
const LIST_CACHE_TTL = 120_000;

const _boardCache: Map<string, { data: SavedTaskBoard; expiry: number }> = new Map();
const BOARD_CACHE_TTL = 120_000;

export function invalidateBoardsCache() {
  _boardsCache = null;
  _boardsCacheExpiry = 0;
  _boardCache.clear();
}

export function invalidateBoardCache(boardId: string) {
  _boardCache.delete(boardId);
}

// The list cache holds the "All Projects" result set. Switching the active
// project must drop it, otherwise navigating back to All Projects can serve a
// list captured under a different filter.
projectStore.onChange(invalidateBoardsCache);

// Kept as a no-op export for backwards-compatibility (logout flows may call it).
export function clearTokenCache() { /* no token cache with direct Supabase */ }

// ── Service (direct Supabase — mirrors whiteboardService) ──────────────────
// RLS scopes every query to the caller's org, so no explicit org filter on reads.

export const taskboardService = {
  async getTaskBoards(searchQuery?: string, projectId?: string | null): Promise<SavedTaskBoard[]> {
    // Cache only the unfiltered, all-projects list (instant re-navigation).
    if (!searchQuery && !projectId) {
      const now = Date.now();
      if (_boardsCache && now < _boardsCacheExpiry) return _boardsCache;
    }

    let query = supabase
      .from('taskboards')
      .select('*')
      .order('updated_at', { ascending: false });

    if (searchQuery) query = query.ilike('name', `%${searchQuery}%`);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) { console.error('Error fetching task boards:', error); throw error; }

    const boards = (data ?? []) as SavedTaskBoard[];
    if (!searchQuery && !projectId) {
      _boardsCache = boards;
      _boardsCacheExpiry = Date.now() + LIST_CACHE_TTL;
    }
    return boards;
  },

  async getTaskBoard(id: string): Promise<SavedTaskBoard | null> {
    const now = Date.now();
    const cached = _boardCache.get(id);
    if (cached && now < cached.expiry) return cached.data;

    const { data, error } = await supabase
      .from('taskboards')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // not found
      console.error('Error fetching task board:', error);
      throw error;
    }
    const board = data as SavedTaskBoard;
    _boardCache.set(id, { data: board, expiry: now + BOARD_CACHE_TTL });
    return board;
  },

  async createTaskBoard(name: string, columns: Column[], projectId?: string | null): Promise<SavedTaskBoard> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const orgId = orgStore.getOrgId();
    if (!orgId) throw new Error('No organization found');

    const { data, error } = await supabase
      .from('taskboards')
      .insert({
        user_id: user.id,
        org_id: orgId,
        name,
        columns,
        project_id: projectId ?? projectStore.getProjectId(),
      })
      .select()
      .single();

    if (error) { console.error('Error creating task board:', error); throw error; }
    invalidateBoardsCache();
    return data as SavedTaskBoard;
  },

  async updateTaskBoard(
    id: string,
    updates: { name?: string; columns?: Column[] },
    _currentUpdatedAt?: string, // kept for call-site compatibility; last-write-wins
  ): Promise<SavedTaskBoard> {
    const { data, error } = await supabase
      .from('taskboards')
      .update({ ...stripImmutable(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select()
      .single();

    if (error) { console.error('Error updating task board:', error); throw error; }

    const board = data as SavedTaskBoard;
    // Keep caches fresh so the list/board reflect the save without a re-fetch.
    if (_boardsCache) {
      _boardsCache = _boardsCache.map(b => b.id === id ? board : b);
    }
    _boardCache.set(id, { data: board, expiry: Date.now() + BOARD_CACHE_TTL });
    return board;
  },

  async deleteTaskBoard(id: string): Promise<void> {
    const { error } = await supabase
      .from('taskboards')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) { console.error('Error deleting task board:', error); throw error; }
    invalidateBoardsCache();
  },

  async duplicateTaskBoard(id: string, newName: string): Promise<SavedTaskBoard> {
    const original = await this.getTaskBoard(id);
    if (!original) throw new Error('Task board not found');
    return this.createTaskBoard(newName, original.columns, original.project_id ?? null);
  },
};
